import React, { useState, useCallback } from "react";
import { useSuperAdmin } from "../context/SuperAdminContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WorkerSearchBar from "../components/WorkerOnboarding/WorkerSearchBar";
import { FilterPanel }     from "../components/WorkerOnboarding/FilterPanel";       
import { useWorkerFilters } from "../hooks/useWorkerFilters";                        
import { WorkerTable }      from "../components/WorkerOnboarding/WorkerTable";
import { useNavigate }      from "react-router-dom";

const PAGE_LIMIT = 20;

const WorkerOnboardingPage = () => {
  const {
    fetchLocations,
    fetchSkills,
    fetchCompaniesByLocations,
    fetchOnboardingWorkers,
    searchWorker,
    downloadWorkersCSV,
    fetchLastCallStatuses,
    fetchLastCallSubStatuses,
    updateWorkerVerificationStatus,
    fetchSources,
  } = useSuperAdmin();

  const navigate = useNavigate();

  // ── All filter state lives here now ───────────────────────────────────────
  const filters = useWorkerFilters({
    fetchLocations,
    fetchSkills,
    fetchCompaniesByLocations,
    fetchLastCallStatuses,
    fetchLastCallSubStatuses,
    fetchSources,
  });

  const { buildOnboardingPayload, clearAllFilters } = filters;

  // ── UI state ──────────────────────────────────────────────────────────────
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [filterApplied,    setFilterApplied]    = useState(false);
  const [applyingFilters,  setApplyingFilters]  = useState(false);
  const [workers,          setWorkers]          = useState([]);
  const [totalWorkers,     setTotalWorkers]      = useState(0);
  const [currentPage,      setCurrentPage]      = useState(1);
  const totalPages = Math.ceil(totalWorkers / PAGE_LIMIT);

  // ── Reset results ─────────────────────────────────────────────────────────
  const resetResults = useCallback(() => {
    setFilterApplied(false);
    setWorkers([]);
    setTotalWorkers(0);
  }, []);

  // ── Fetch workers ─────────────────────────────────────────────────────────
  const fetchWorkers = useCallback(async (page = 1, overrides = {}) => {
    setApplyingFilters(true);
    try {
      const payload = buildOnboardingPayload(page, overrides);
      console.log("📤 /api/on-boarding/filters payload:", JSON.stringify(payload, null, 2));

      const data = await fetchOnboardingWorkers(payload);
      const list = Array.isArray(data?.data)    ? data.data
                 : Array.isArray(data?.workers)  ? data.workers
                 : Array.isArray(data?.results)  ? data.results
                 : [];

      const enriched = list.map(w => ({
        ...w,
        lastStatus: w.lastStatus || { status: null, remarks: null, updatedBy: null, updatedAt: null },
      }));

      setWorkers(enriched);
      setTotalWorkers(data?.total ?? data?.count ?? enriched.length);
      setFilterApplied(true);
      setCurrentPage(page);
    } catch (e) {
      console.error("Failed to fetch workers:", e);
      toast.error("Failed to fetch workers. Please try again.");
    } finally {
      setApplyingFilters(false);
    }
  }, [buildOnboardingPayload, fetchOnboardingWorkers]);

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (query, searchType) => {
    if (!query?.trim()) return;
    setApplyingFilters(true);
    try {
      const data = await searchWorker({
        [searchType ]: query.trim(),
      });
      if (data?.success && data?.data) {
        const list = Array.isArray(data.data) ? data.data : [data.data];
        setWorkers(list);
        setTotalWorkers(list.length);
        setFilterApplied(true);
        setCurrentPage(1);
        toast.success(`Found ${list.length} worker(s) by ${searchType}`);
      } else {
        setWorkers([]); setTotalWorkers(0); setFilterApplied(true);
        toast.info(`No worker found with this ${searchType}`);
      }
    } catch (err) {
      toast.error("Search failed. Please try again.");
      setWorkers([]); setTotalWorkers(0);
    } finally {
      setApplyingFilters(false);
    }
  }, [searchWorker]);

  // ── Download CSV ──────────────────────────────────────────────────────────
  const handleDownloadCsv = useCallback(async () => {
    try {
      const payload = buildOnboardingPayload(currentPage);
      toast.info("Preparing CSV download...");
      const blob = await downloadWorkersCSV(payload);
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", `workers_${new Date().toISOString().slice(0,19).replace(/:/g,"-")}.csv`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded successfully!");
    } catch (err) {
      toast.error("Failed to download CSV. Please try again.");
    }
  }, [buildOnboardingPayload, currentPage, downloadWorkersCSV]);

  // ── Update last call status locally ──────────────────────────────────────
  const handleUpdateLastCallStatus = useCallback((workerId, newStatus, remarks) => {
    setWorkers(prev => prev.map(w =>
      w.workerId === workerId
        ? { ...w, lastStatus: { ...w.lastStatus, status: newStatus, remarks, updatedAt: new Date().toISOString() } }
        : w
    ));
  }, []);

  const handleRowClick   = useCallback((id) => window.open(`/worker/${id}`, "_blank", "noopener,noreferrer"), []);
  const handleFilterApply = () => fetchWorkers(1);
  const handlePageChange  = (page) => fetchWorkers(page);
  const handleClearAll    = () => { clearAllFilters(resetResults); toast.success("All filters cleared"); };
  
  // ── Navigation handlers ──────────────────────────────────────────────────
  const handlePerformanceReport = () => {
    navigate("/lkart/superadmin/performance-report");
  };

  const canApply =
    filters.selectedLocations.length > 0 &&
    filters.selectedSkills.length > 0 &&
    filters.selectedGenders.length > 0 &&
    filters.selectedSources.length > 0 &&
    filters.selectedWorkerTypes.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-8xl mx-auto px-6 py-8 mt-4">
        <div className="bg-white p-6 rounded-xl shadow-md">

          {/* Header with title centered and button on the right */}
          <div className="relative mb-4">
            <h2 className="text-2xl font-semibold text-gray-800 text-center">Worker Onboarding</h2>
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <button
                onClick={handlePerformanceReport}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Performance Report
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="text-center mb-6">
            <div className="inline-block w-full max-w-2xl">
              <WorkerSearchBar onSearch={handleSearch} isSearching={applyingFilters} placeholder="Search" />
            </div>
          </div>

          {/* Collapsible Filters */}
          <div className="border border-gray-200 rounded-lg mb-6">
            <button
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
            >
              <div className="flex items-center gap-2">
                <svg className={`w-5 h-5 text-gray-500 transition-transform ${filtersCollapsed ? "" : "rotate-180"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="font-medium text-gray-700">Filters</span>
                <span className="text-xs text-gray-400">
                  {filtersCollapsed ? "(click to expand)" : "(click to collapse)"}
                </span>
              </div>

              {filtersCollapsed && (
                <div className="flex items-center gap-3">
                  {filterApplied && (
                    <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-gray-200">
                      <span className="text-sm font-semibold text-gray-700">
                        Total: <span className="text-indigo-600">{totalWorkers}</span>
                      </span>
                    </div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                    className="bg-gray-500 text-white px-4 py-1.5 rounded-lg hover:bg-gray-600 text-sm font-medium">
                    Clear All Filters
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleFilterApply(); }}
                    disabled={applyingFilters || !canApply}
                    className="bg-indigo-500 text-white px-4 py-1.5 rounded-lg hover:bg-indigo-600 disabled:opacity-50 text-sm font-medium">
                    {applyingFilters ? "Applying..." : "Apply Filters"}
                  </button>
                </div>
              )}

              {!filtersCollapsed && filterApplied && (
                <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">
                    Total: <span className="text-indigo-600">{totalWorkers}</span>
                  </span>
                </div>
              )}
            </button>

            {!filtersCollapsed && (
              <div className="p-4 border-t border-gray-200">

                {/* ── Shared filter grid ── */}
                <FilterPanel
                  filters={filters}
                  onReset={resetResults}
                  showOnboardingFilters={true}   // full set
                />

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    {filterApplied && (
                      <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-300">
                        <span className="text-sm font-semibold text-gray-700">
                          Total Workers: <span className="text-indigo-600">{totalWorkers}</span>
                        </span>
                      </div>
                    )}
                    {filterApplied && (
                      <button onClick={handleDownloadCsv}
                        className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 hover:border-green-500 hover:text-green-700 text-sm font-medium">
                        Download CSV
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3 w-full sm:w-auto">
                    <button onClick={handleClearAll}
                      className="bg-gray-500 text-white px-6 py-2.5 rounded-lg hover:bg-gray-600 font-medium">
                      Clear All Filters
                    </button>
                    <button onClick={handleFilterApply} disabled={applyingFilters || !canApply}
                      className="bg-indigo-500 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-600 disabled:opacity-50 font-medium">
                      {applyingFilters ? "Applying..." : "Apply Filters"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {filterApplied && (
            <WorkerTable
              workers={workers}
              currentPage={currentPage}
              pageSize={PAGE_LIMIT}
              totalPages={totalPages}
              setCurrentPage={handlePageChange}
              loading={applyingFilters}
              showCompanyCheck={filters.companyCheckEnabled}
              onUpdateLastCallStatus={handleUpdateLastCallStatus}
              lastCallStatusOptions={filters.lastCallStatusOptions}
              fetchLastCallStatuses={fetchLastCallStatuses}
              fetchLastCallSubStatuses={fetchLastCallSubStatuses}
              updateWorkerVerificationStatus={updateWorkerVerificationStatus}
              onRowClick={handleRowClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerOnboardingPage;